import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { uploadToCloudinary } from '../lib/cloudinary'
import { useAuth } from '../contexts/AuthContext'
import StatusBadge from '../components/StatusBadge'
import EmbedBadge from '../components/EmbedBadge'
import VideoEmbed from '../components/VideoEmbed'
import ContactForm from '../components/ContactForm'
import './Project.css'

// Every creator gets every commit type and every visibility option — free, for everyone.
const ALL_COMMIT_TYPES = ['text', 'video', 'link', 'photo']

const COMMIT_TYPE_LABELS = {
  text: '📝 Note',
  video: '▶ Video',
  link: '🔗 Link',
  photo: '📷 Photo',
}

function isVideoUrl(url) {
  return /(?:youtube\.com|youtu\.be|vimeo\.com)/i.test(url)
}

const VISIBILITY_LABELS = {
  public:   { label: 'Public',   desc: 'Visible to everyone', icon: '🌐' },
  unlisted: { label: 'Unlisted', desc: 'Accessible by link only', icon: '🔗' },
  private:  { label: 'Private',  desc: 'Only you can see this', icon: '🔒' },
}

function generateUniqueId() {
  return 'HO-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).substring(2, 6).toUpperCase()
}

export default function Project() {
  const { projectId } = useParams()
  const { user, profile, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const isNew = projectId === 'new'

  const [project, setProject] = useState(null)
  const [commits, setCommits] = useState([])
  const [loading, setLoading] = useState(false)
  const [notFound, setNotFound] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [reviewGateRequired, setReviewGateRequired] = useState('2')
  const [showSellForm, setShowSellForm] = useState(false)
  const [sellPrice, setSellPrice] = useState('')
  const [savingSale, setSavingSale] = useState(false)
  const [showBuyInterest, setShowBuyInterest] = useState(false)
  const [showHideBox, setShowHideBox] = useState(false)
  const [hideReason, setHideReason] = useState('')
  const [savingHide, setSavingHide] = useState(false)
  const isAdmin = !!profile?.is_admin
  const [success, setSuccess] = useState('')

  const [form, setForm] = useState({ title: '', category: '', custom_category: '', description: '', visibility: 'public' })
  const [bannerFile, setBannerFile] = useState(null)
  const [bannerPreview, setBannerPreview] = useState(null)
  const [showBannerUpload, setShowBannerUpload] = useState(false)
  const [commitMsg, setCommitMsg] = useState('')
  const [commitType, setCommitType] = useState('text')
  const [commitUrl, setCommitUrl] = useState('')
  const [commitFile, setCommitFile] = useState(null)
  const [addingCommit, setAddingCommit] = useState(false)
  const [showCommitForm, setShowCommitForm] = useState(false)

  // ---- Edit commit state ----
  const [editingCommitId, setEditingCommitId] = useState(null)
  const [editCommitMsg, setEditCommitMsg] = useState('')
  const [editCommitUrl, setEditCommitUrl] = useState('')
  const [savingCommit, setSavingCommit] = useState(false)
  const [now, setNow] = useState(Date.now())

  // ---- Edit details state (verified projects) ----
  const [showEditDetails, setShowEditDetails] = useState(false)
  const [editForm, setEditForm] = useState({})
  const [editBannerFile, setEditBannerFile] = useState(null)
  const [editBannerPreview, setEditBannerPreview] = useState(null)
  const [editSaving, setEditSaving] = useState(false)

  // ---- Delete project state ----
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    if (authLoading) return
    if (isNew && !user) { navigate('/login'); return }
    if (!isNew) fetchProject()
  }, [authLoading, user, projectId])

  const isOwner = user && project && user.id === project.user_id
  const allowedCommitTypes = ALL_COMMIT_TYPES
  const canUnlist = true
  const canPrivate = true

  async function fetchProject(silent = false) {
    if (!silent) setLoading(true)
    setNotFound(false)
    const { data: proj } = await supabase
      .from('projects')
      .select('*, profiles!projects_user_id_fkey(full_name, display_name, username)')
      .eq('id', projectId)
      .single()
    if (!proj) { setNotFound(true); setLoading(false); return }
    if (proj.visibility === 'private' && (!user || user.id !== proj.user_id)) {
      setNotFound(true); setLoading(false); return
    }
    setProject(proj)
    const { data: cms } = await supabase
      .from('commits')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true })
    setCommits(cms || [])
    setLoading(false)
  }

  async function handleCreateProject(e) {
    e.preventDefault()
    if (!form.title || !form.category) { setError('Title and category are required.'); return }

    setSaving(true)
    setError('')
    try {
      const { data, error: err } = await supabase.from('projects').insert({
        user_id: user.id,
        unique_id: generateUniqueId(),
        title: form.title,
        category: form.category,
        custom_category: form.custom_category,
        description: form.description,
        visibility: form.visibility,
        show_evidence_on_cert: true,
        status: 'ongoing',
      }).select().single()
      if (err) throw err
      navigate(`/project/${data.id}`)
    } catch (err) {
      setError(err.message || 'Could not create project. Please try again.')
      setSaving(false)
    }
  }

  async function handleAddCommit(e) {
    e.preventDefault()
    const finalCommitType = commitType === 'link' && isVideoUrl(commitUrl) ? 'video' : commitType
    if (!commitMsg) { setError('Commit message is required.'); return }
    if (['video', 'link'].includes(finalCommitType) && !commitUrl) {
      setError(finalCommitType === 'video' ? 'Video URL is required.' : 'Link URL is required.')
      return
    }
    if (finalCommitType === 'photo' && !commitFile) { setError('Photo is required.'); return }
    setAddingCommit(true)
    setError('')
    let mediaUrl = commitUrl || null

    if (finalCommitType === 'photo' && commitFile) {
      mediaUrl = await uploadToCloudinary(commitFile, 'commit-media')
      if (!mediaUrl) { setError('Photo upload failed'); setAddingCommit(false); return }
    }

    const { data: newCommit, error: err } = await supabase.from('commits').insert({
      project_id: project.id,
      user_id: user.id,
      message: commitMsg,
      commit_type: finalCommitType,
      media_url: mediaUrl,
    }).select().single()
    if (err) { setError(err.message); setAddingCommit(false); return }
    setCommits(prev => [...prev, newCommit])
    setCommitMsg(''); setCommitUrl(''); setCommitFile(null); setShowCommitForm(false)
    setSuccess('Commit added!')
    setTimeout(() => setSuccess(''), 2000)
    fetchProject(true) // background resync, no longer what the button depends on
    setAddingCommit(false)
  }

  // ---- Tick clock every second for smooth countdown ----
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  const EDIT_WINDOW_MS = 15 * 60 * 1000 // 15 minutes

  function getEditSecondsLeft(commit) {
    const age = now - new Date(commit.created_at).getTime()
    return Math.max(0, Math.ceil((EDIT_WINDOW_MS - age) / 1000))
  }

  function formatCountdown(seconds) {
    if (seconds <= 0) return null
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return m > 0 ? `${m}m ${s}s` : `${s}s`
  }

  async function handleEditCommit(commit) {
    setSavingCommit(true)
    setError('')
    const secsLeft = getEditSecondsLeft(commit)
    if (secsLeft <= 0) { setError('Edit window has closed for this commit.'); setSavingCommit(false); return }
    const { error: err } = await supabase.from('commits')
      .update({ message: editCommitMsg, ...(commit.commit_type !== 'photo' ? { media_url: editCommitUrl || null } : {}) })
      .eq('id', commit.id)
      .eq('user_id', user.id)
    if (err) { setError(err.message); setSavingCommit(false); return }
    setEditingCommitId(null)
    setSuccess('Commit updated!')
    setTimeout(() => setSuccess(''), 2000)
    await fetchProject(true)
    setSavingCommit(false)
  }

  async function handleDeleteCommit(commit) {
    const secsLeft = getEditSecondsLeft(commit)
    if (secsLeft <= 0) { setError('Edit window has closed — this commit is now locked.'); return }
    if (!window.confirm('Delete this commit? This cannot be undone.')) return
    const { error: err } = await supabase.from('commits')
      .delete()
      .eq('id', commit.id)
      .eq('user_id', user.id)
    if (err) { setError(err.message); return }
    setSuccess('Commit deleted.')
    setTimeout(() => setSuccess(''), 2000)
    await fetchProject(true)
  }

  async function handleSubmitForReview() {
    if (!project || commits.length < 3) { setError(`At least 3 commits are required before submitting — you have ${commits.length}. More commits means stronger evidence.`); return }
    setSaving(true)
    let banner_url = project.banner_url || null
    if (bannerFile) banner_url = await uploadToCloudinary(bannerFile, 'project-banners')
    const { error: submitErr } = await supabase.from('projects').update({ status: 'submitted', submitted_at: new Date().toISOString(), ...(banner_url ? { banner_url } : {}) }).eq('id', project.id)
    if (submitErr) {
      if (submitErr.message?.includes('Review at least')) {
        const match = submitErr.message.match(/Review at least (\d+)/)
        setReviewGateRequired(match ? match[1] : '2')
        setError('review_gate')
      } else {
        setError(submitErr.message)
      }
      setSaving(false)
      return
    }
    fetchProject()
    setSaving(false)
    setShowBannerUpload(false)
    setSuccess('Submitted for review! You will be notified once reviewed.')
  }

  async function handleWithdrawSubmission() {
    if (!window.confirm('Withdraw your submission? Your project will return to ongoing and you can add more commits before resubmitting.')) return
    setSaving(true)
    await supabase.from('projects').update({ status: 'ongoing', submitted_at: null }).eq('id', project.id)
    fetchProject()
    setSaving(false)
    setSuccess('Submission withdrawn. You can now add more commits and resubmit when ready.')
  }

  async function handleListForSale() {
    const priceNum = parseFloat(sellPrice)
    if (!priceNum || priceNum <= 0) { setError('Enter a valid price.'); return }
    setSavingSale(true)
    const { error: err } = await supabase.from('projects').update({ for_sale: true, price: priceNum }).eq('id', project.id)
    setSavingSale(false)
    if (err) { setError(err.message); return }
    setProject(p => ({ ...p, for_sale: true, price: priceNum }))
    setShowSellForm(false)
    setSuccess('Listed for sale!')
  }

  async function handleRemoveFromSale() {
    setSavingSale(true)
    await supabase.from('projects').update({ for_sale: false }).eq('id', project.id)
    setSavingSale(false)
    setProject(p => ({ ...p, for_sale: false }))
    setSuccess('Removed from sale.')
  }

  async function handleHideProject() {
    if (savingHide) return
    setSavingHide(true)
    const { error: err } = await supabase.from('projects').update({
      admin_hidden: true,
      admin_hidden_reason: hideReason || null,
      admin_hidden_by: user.id,
      admin_hidden_at: new Date().toISOString(),
    }).eq('id', project.id)
    setSavingHide(false)
    if (err) { setError(err.message); return }
    setProject(p => ({ ...p, admin_hidden: true, admin_hidden_reason: hideReason || null }))
    setShowHideBox(false)
    setSuccess('Project hidden from Wall and Browse.')
  }

  async function handleUnhideProject() {
    if (savingHide) return
    setSavingHide(true)
    const { error: err } = await supabase.from('projects').update({
      admin_hidden: false, admin_hidden_reason: null, admin_hidden_by: null, admin_hidden_at: null,
    }).eq('id', project.id)
    setSavingHide(false)
    if (err) { setError(err.message); return }
    setProject(p => ({ ...p, admin_hidden: false, admin_hidden_reason: null }))
    setSuccess('Project restored to Wall and Browse.')
  }

  async function handleVisibilityChange(newVis) {
    if (newVis === 'unlisted' && !canUnlist) return
    if (newVis === 'private' && !canPrivate) return
    await supabase.from('projects').update({ visibility: newVis }).eq('id', project.id)
    setProject(p => ({ ...p, visibility: newVis }))
  }

  async function handleEvidenceCertChange(enabled) {
    const { error: err } = await supabase
      .from('projects')
      .update({ show_evidence_on_cert: enabled })
      .eq('id', project.id)
    if (err) { setError(err.message); return }
    setProject(p => ({ ...p, show_evidence_on_cert: enabled }))
  }

  // ---- Save edited project details (verified projects only) ----
  async function handleSaveDetails(e) {
    e.preventDefault()
    if (!editForm.title || !editForm.category) { setError('Title and category are required.'); return }
    setEditSaving(true)
    setError('')
    try {
      let banner_url = project.banner_url || null
      if (editBannerFile) {
        banner_url = await uploadToCloudinary(editBannerFile, 'project-banners')
        if (!banner_url) throw new Error('Banner upload failed. Please try again.')
      }
      const { error: err } = await supabase.from('projects').update({
        title: editForm.title,
        category: editForm.category,
        custom_category: editForm.custom_category || null,
        description: editForm.description || null,
        banner_url,
        updated_at: new Date().toISOString(),
      }).eq('id', project.id)
      if (err) throw err
      setSuccess('Project details updated!')
      setShowEditDetails(false)
      setEditBannerFile(null)
      setEditBannerPreview(null)
      setTimeout(() => setSuccess(''), 3000)
      fetchProject()
    } catch (err) {
      setError(err.message || 'Could not save changes.')
    } finally {
      setEditSaving(false)
    }
  }

  // ---- Delete project ----
  async function handleDeleteProject() {
    if (deleteInput !== project.title) return
    setDeleting(true)
    setError('')
    try {
      const { error: err } = await supabase.from('projects').delete().eq('id', project.id)
      if (err) throw err
      navigate('/dashboard')
    } catch (err) {
      setError(err.message || 'Could not delete project. Please try again.')
      setDeleting(false)
    }
  }

  // ---- STEP 1: wait for auth ----
  if (authLoading) return (
    <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
  )

  // ---- STEP 2: new project form (must be before loading/notFound checks) ----
  if (isNew) return (
    <div className="project-page page-fade">
      <div className="container">
        <div className="project-new-header">
          <h1>Start a new project</h1>
          <p>Create your project and document your creative process with commits. When you're done, submit it for human review.</p>
        </div>
        {error && <div className="error-msg" style={{ marginBottom: 20 }}>{error}</div>}
        {(profile?.reviews_completed_count || 0) < 2 && (
          <div className="info-msg" style={{ marginBottom: 20 }}>
            Heads up — you'll need to review 2 other people's projects before this one can be submitted for verification. Doesn't need to happen now; just get it done before you hit submit. <Link to="/review" style={{ textDecoration: 'underline' }}>Review now →</Link>
          </div>
        )}
        <div className="project-new-layout">
          <form className="card" onSubmit={handleCreateProject}>
            <div className="form-group">
              <label>Project title *</label>
              <input type="text" placeholder="e.g. Mountain oil painting, Lo-fi EP, Logo for Acme" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Category *</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} required>
                <option value="">Select a category...</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {form.category === 'Other' && (
              <div className="form-group">
                <label>Describe your category</label>
                <input type="text" placeholder="e.g. Calligraphy, Tapestry..." value={form.custom_category} onChange={e => setForm(f => ({ ...f, custom_category: e.target.value }))} />
              </div>
            )}
            <div className="form-group">
              <label>Description</label>
              <textarea rows={3} placeholder="Tell us about this project..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Visibility</label>
              <div className="visibility-options">
                {['public', 'unlisted', 'private'].map(vis => {
                  const locked = (vis === 'unlisted' && !canUnlist) || (vis === 'private' && !canPrivate)
                  return (
                    <label key={vis} className={`vis-option ${form.visibility === vis ? 'selected' : ''} ${locked ? 'locked' : ''}`}>
                      <input type="radio" name="visibility" checked={form.visibility === vis} disabled={locked} onChange={() => !locked && setForm(f => ({ ...f, visibility: vis }))} />
                      <div className="vis-option-inner">
                        <strong>{VISIBILITY_LABELS[vis].icon} {VISIBILITY_LABELS[vis].label} {locked && <span className="coming-soon-flag">Creator+</span>}</strong>
                        <span>{VISIBILITY_LABELS[vis].desc}</span>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
            <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={saving}>
              {saving ? <span className="loading-spinner" /> : 'Create project →'}
            </button>
          </form>
          <div className="project-new-sidebar">
            <div className="card">
              <h3>How commits work</h3>
              <p style={{ color: 'var(--text-2)', fontSize: 14, lineHeight: 1.7 }}>
                Once your project is created, document your process with commits — notes, photos, or links. Each commit is timestamped and can't be backdated.<br /><br />
                When you're done, submit for human review. The commit history is your proof of creation.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  // ---- STEP 3: loading existing project ----
  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}><span className="loading-spinner" /></div>
  )

  // ---- STEP 4: not found ----
  if (notFound) return (
    <div className="container" style={{ textAlign: 'center', padding: 80 }}>
      <h2>Project not found</h2>
      <p style={{ color: 'var(--text-2)', marginTop: 8 }}>This project is private or doesn't exist.</p>
      <Link to="/browse" className="btn btn-secondary" style={{ marginTop: 24 }}>Browse verified work</Link>
    </div>
  )

  // ---- STEP 5: project must exist by here ----
  if (!project) return null

  const creatorName = project.profiles?.display_name || project.profiles?.full_name || 'Anonymous'

  return (
    <div className="project-page page-fade">
      <div className="container">
        <div className="project-header">
          <div className="project-breadcrumb">
            {project.profiles?.username && (
              <><Link to={`/u/${project.profiles.username}`}>{creatorName}</Link> <span>/</span></>
            )}
            <span>{project.title}</span>
          </div>
          <h1>{project.title}</h1>
          <div className="project-meta">
            <span className="project-category">{project.category === 'Other (describe below)' ? 'Other' : project.category}{project.custom_category ? ` / ${project.custom_category}` : ''}</span>
            <StatusBadge status={project.status} />
            <span className="project-vis-tag">{VISIBILITY_LABELS[project.visibility]?.icon} {VISIBILITY_LABELS[project.visibility]?.label}</span>
          </div>
          <div className="project-header-actions">
            {isOwner && (project.status === 'ongoing' || project.status === 'needs_evidence') && (
              <button className="btn btn-primary" onClick={() => setShowCommitForm(!showCommitForm)}>
                + Add commit
              </button>
            )}
            {isOwner && (project.status === 'ongoing' || project.status === 'needs_evidence') && (
              <button
                className="btn btn-primary"
                onClick={() => setShowBannerUpload(true)}
                disabled={saving || commits.length < 3}
                style={commits.length < 3 ? { opacity: 0.4, cursor: 'not-allowed', filter: 'grayscale(1)' } : {}}
              >
                Submit for review →
              </button>
            )}
            {isOwner && project.status === 'submitted' && (
              <button className="btn btn-secondary" onClick={handleWithdrawSubmission} disabled={saving}>
                Withdraw submission
              </button>
            )}
            {isOwner && (project.status === 'ongoing' || project.status === 'needs_evidence' || project.status === 'submitted') && (
              <button className="btn btn-secondary" disabled title="Available once this project is verified"
                style={{ opacity: 0.4, cursor: 'not-allowed', filter: 'grayscale(1)' }}>
                Sell this work
              </button>
            )}
            {isOwner && project.status === 'verified' && !project.for_sale && (
              <button className="btn btn-secondary" onClick={() => setShowSellForm(v => !v)}>
                {showSellForm ? 'Cancel' : 'Sell this work'}
              </button>
            )}
            {isOwner && project.status === 'verified' && project.for_sale && (
              <button className="btn btn-secondary" onClick={handleRemoveFromSale} disabled={savingSale}>
                For sale: ${project.price} · Remove from sale
              </button>
            )}
            {project.status === 'verified' && isOwner && (
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setEditForm({
                    title: project.title,
                    category: project.category,
                    custom_category: project.custom_category || '',
                    description: project.description || '',
                  })
                  setEditBannerPreview(project.banner_url || null)
                  setEditBannerFile(null)
                  setShowEditDetails(true)
                }}
              >
                ✏️ Edit details
              </button>
            )}
            {project.status === 'verified' && (
              <Link to={`/certificate/${project.unique_id}`} className="btn btn-primary">View certificate →</Link>
            )}
          </div>
        </div>

        {isOwner && showSellForm && (
          <div className="card" style={{ marginTop: 12, marginBottom: 16 }}>
            <h3 style={{ marginBottom: 12, fontSize: 16 }}>List this work for sale</h3>
            <div className="form-group">
              <label>Price (USD)</label>
              <input
                type="number"
                min="1"
                step="0.01"
                placeholder="e.g. 150"
                value={sellPrice}
                onChange={e => setSellPrice(e.target.value)}
              />
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 12 }}>
              This is a proof of concept — buyers reach you through your existing enquiry inbox. No payment is processed here yet.
            </p>
            <button className="btn btn-primary" onClick={handleListForSale} disabled={savingSale}>
              {savingSale ? <span className="loading-spinner" /> : 'List for sale →'}
            </button>
          </div>
        )}

        {!isOwner && project.status === 'verified' && project.for_sale && (
          <div className="card" style={{ marginTop: 12, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <span style={{ fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>For sale</span>
              <div style={{ fontSize: 22, fontWeight: 700 }}>${project.price}</div>
            </div>
            <button className="btn btn-primary" onClick={() => setShowBuyInterest(v => !v)}>
              {showBuyInterest ? 'Cancel' : "I'm interested →"}
            </button>
          </div>
        )}

        {!isOwner && showBuyInterest && (
          <div className="card" style={{ marginBottom: 16 }}>
            <ContactForm
              creatorId={project.user_id}
              creatorName={project.profiles?.display_name || project.profiles?.full_name || 'the creator'}
              initialMessage={`Hi, I'm interested in buying "${project.title}" (listed at $${project.price}). `}
            />
          </div>
        )}

        {error && (
          error === 'review_gate' ? (
            <div className="error-msg" style={{ marginBottom: 16 }}>
              You need to review {reviewGateRequired} more project{reviewGateRequired === '1' ? '' : 's'} before you can submit your own — it keeps the review queue moving for everyone.{' '}
              <Link to="/review" style={{ textDecoration: 'underline' }}>Head to the review queue →</Link>
            </div>
          ) : (
            <div className="error-msg" style={{ marginBottom: 16 }}>{error}</div>
          )
        )}
        {success && <div className="success-msg" style={{ marginBottom: 16 }}>{success}</div>}

        {project.admin_hidden && isOwner && !isAdmin && (
          <div className="error-msg" style={{ marginBottom: 16 }}>
            This project has been hidden from the Wall and Browse by a moderator{project.admin_hidden_reason ? ` — "${project.admin_hidden_reason}"` : ''}. Your certificate link (if verified) still works. You can keep editing this project as normal.
          </div>
        )}

        {isAdmin && (
          <div className="card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <span style={{ fontSize: 12, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Admin</span>
              <div style={{ fontSize: 14 }}>
                {project.admin_hidden
                  ? `Hidden from Wall/Browse${project.admin_hidden_reason ? ` — "${project.admin_hidden_reason}"` : ''}`
                  : 'Visible on Wall/Browse'}
              </div>
            </div>
            {project.admin_hidden ? (
              <button className="btn btn-secondary btn-sm" onClick={handleUnhideProject} disabled={savingHide}>
                Unhide project
              </button>
            ) : (
              <button className="btn btn-danger btn-sm" onClick={() => setShowHideBox(v => !v)} disabled={savingHide}>
                {showHideBox ? 'Cancel' : 'Hide from Wall/Browse'}
              </button>
            )}
          </div>
        )}

        {isAdmin && showHideBox && !project.admin_hidden && (
          <div className="card" style={{ marginBottom: 16 }}>
            <textarea
              placeholder="Reason for hiding (shown to the creator)"
              value={hideReason}
              onChange={e => setHideReason(e.target.value)}
              rows={2}
              style={{ marginBottom: 10 }}
            />
            <button className="btn btn-danger btn-sm" onClick={handleHideProject} disabled={savingHide}>
              {savingHide ? <span className="loading-spinner" /> : 'Confirm hide'}
            </button>
          </div>
        )}


        {isOwner && (project.status === 'ongoing' || project.status === 'needs_evidence') && (profile?.reviews_completed_count || 0) < 2 && (
          <div className="info-msg" style={{ marginBottom: 16 }}>
            Review 2 other projects before this one can be submitted — {profile?.reviews_completed_count || 0}/2 done. <Link to="/review" style={{ textDecoration: 'underline' }}>Review now →</Link>
          </div>
        )}

        {showBannerUpload && (
          <div className="card" style={{ marginBottom: 24, padding: 24 }}>
            <h3 style={{ marginBottom: 4 }}>Add a project banner</h3>
            <p style={{ color: 'var(--text-2)', fontSize: 14, marginBottom: 16 }}>This image will be shown on the Browse page. You can skip it and add one later.</p>
            <div
              onClick={() => document.getElementById('submit-banner-input').click()}
              style={{ cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px dashed var(--border)', background: 'var(--bg-3)', minHeight: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}
            >
              {bannerPreview
                ? <img src={bannerPreview} alt="Banner preview" style={{ width: '100%', height: 140, objectFit: 'cover', display: 'block' }} />
                : <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-3)' }}>
                    <div style={{ fontSize: 28, marginBottom: 8 }}>🖼️</div>
                    <div style={{ fontSize: 13 }}>Click to upload a banner image</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Recommended: 1200 × 630px</div>
                  </div>
              }
            </div>
            <input id="submit-banner-input" type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
              const f = e.target.files[0]
              if (f) { setBannerFile(f); setBannerPreview(URL.createObjectURL(f)) }
            }} />
            {bannerPreview && (
              <button type="button" className="btn btn-ghost btn-sm" style={{ marginBottom: 12 }} onClick={() => { setBannerFile(null); setBannerPreview(null) }}>Remove banner</button>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSubmitForReview} disabled={saving}>
                {saving ? <span className="loading-spinner" /> : 'Submit for review →'}
              </button>
              <button className="btn btn-ghost" onClick={() => { setShowBannerUpload(false); setBannerFile(null); setBannerPreview(null) }}>Cancel</button>
            </div>
          </div>
        )}

        {/* ---- Edit details panel (verified projects only) ---- */}
        {showEditDetails && isOwner && project.status === 'verified' && (
          <div className="card" style={{ marginBottom: 24, padding: 24 }}>
            <h3 style={{ marginBottom: 4 }}>Edit project details</h3>
            <p style={{ color: 'var(--text-2)', fontSize: 13, marginBottom: 20 }}>
              Update the title, description, category, or banner. Commits cannot be changed — they are your verified proof of creation.
            </p>
            <form onSubmit={handleSaveDetails}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={editForm.title || ''}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={editForm.category || ''}
                  onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                  required
                >
                  <option value="">Select a category...</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {editForm.category === 'Other' && (
                <div className="form-group">
                  <label>Describe your category</label>
                  <input
                    type="text"
                    placeholder="e.g. Calligraphy, Tapestry..."
                    value={editForm.custom_category || ''}
                    onChange={e => setEditForm(f => ({ ...f, custom_category: e.target.value }))}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Description</label>
                <textarea
                  rows={4}
                  placeholder="Tell us about this project..."
                  value={editForm.description || ''}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Banner image</label>
                <div
                  onClick={() => document.getElementById('edit-banner-input').click()}
                  style={{ cursor: 'pointer', borderRadius: 10, overflow: 'hidden', border: '1px dashed var(--border)', background: 'var(--bg-3)', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}
                >
                  {editBannerPreview
                    ? <img src={editBannerPreview} alt="Banner preview" style={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }} />
                    : <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-3)' }}>
                        <div style={{ fontSize: 24, marginBottom: 6 }}>🖼️</div>
                        <div style={{ fontSize: 13 }}>Click to upload a new banner</div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>Recommended: 1200 × 630px</div>
                      </div>
                  }
                </div>
                <input
                  id="edit-banner-input"
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files[0]
                    if (f) { setEditBannerFile(f); setEditBannerPreview(URL.createObjectURL(f)) }
                  }}
                />
                {editBannerPreview && (
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    style={{ marginTop: 4 }}
                    onClick={() => { setEditBannerFile(null); setEditBannerPreview(null) }}
                  >
                    Remove banner
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="submit" className="btn btn-primary" disabled={editSaving}>
                  {editSaving ? <span className="loading-spinner" /> : 'Save changes'}
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { setShowEditDetails(false); setEditBannerFile(null); setEditBannerPreview(null) }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {project.admin_message && (
          <div className="info-msg" style={{ marginBottom: 24 }}>
            <strong>Message from reviewer:</strong> {project.admin_message}
            {project.status === 'needs_evidence' && isOwner && (
              <span style={{ display: 'block', marginTop: 8 }}>
                Add more commits above, then resubmit.
                <button className="btn btn-primary btn-sm" style={{ marginLeft: 12 }} onClick={handleSubmitForReview} disabled={saving}>Resubmit →</button>
              </span>
            )}
          </div>
        )}

        <div className="project-layout">
          <div className="project-main">
            {project.description && (
              <div className="card" style={{ marginBottom: 24 }}>
                <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{project.description}</p>
              </div>
            )}

            {isOwner && showCommitForm && (
              <div className="card" style={{ marginBottom: 24 }}>
                <h3 style={{ marginBottom: 16 }}>Add a commit</h3>
                <form onSubmit={handleAddCommit}>
                  <div className="form-group">
                    <label>Type</label>
                    <div className="commit-type-tabs">
                      {allowedCommitTypes.map(t => (
                        <button key={t} type="button" className={`commit-type-tab ${commitType === t ? 'active' : ''}`} onClick={() => setCommitType(t)}>
                          {COMMIT_TYPE_LABELS[t]}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>What did you do? *</label>
                    <textarea rows={3} placeholder="e.g. Finished the rough sketch, adjusted composition..." value={commitMsg} onChange={e => setCommitMsg(e.target.value)} required />
                  </div>
                  {commitType === 'link' && (
                    <div className="form-group">
                      <label>Evidence URL *</label>
                      <input type="url" placeholder="https://drive.google.com/..." value={commitUrl} onChange={e => setCommitUrl(e.target.value)} required />
                      <span className="hint">
                        {isVideoUrl(commitUrl)
                          ? 'This looks like a video URL, so it will be saved as a playable Video commit.'
                          : 'Use this for normal evidence links like Google Drive, Dropbox, GitHub, docs, or portfolio pages.'}
                      </span>
                    </div>
                  )}
                  {commitType === 'video' && (
                    <div className="form-group">
                      <label>Video URL *</label>
                      <input type="url" placeholder="https://www.youtube.com/watch?v=..." value={commitUrl} onChange={e => setCommitUrl(e.target.value)} required />
                      <span className="hint">YouTube and Vimeo links are embedded on your project wall and playable by visitors.</span>
                    </div>
                  )}
                  {commitType === 'photo' && (
                    <div className="form-group">
                      <label>Photo</label>
                      <input type="file" accept="image/*" style={{ padding: '8px 12px' }} onChange={e => setCommitFile(e.target.files[0])} />
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={addingCommit}>
                      {addingCommit ? <span className="loading-spinner" /> : 'Commit →'}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowCommitForm(false)}>Cancel</button>
                  </div>
                  <p className="commit-lock-notice">Commits can be edited or deleted within 15 minutes of posting — after that they're permanent.</p>
                </form>
              </div>
            )}

            <div className="card">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: commits.length < 3 && isOwner ? 8 : 20 }}>
                <h3>Commit history <span style={{ color: 'var(--text-3)', fontWeight: 400, fontSize: 14 }}>({commits.length} update{commits.length !== 1 ? 's' : ''})</span></h3>
              </div>
              {isOwner && (project.status === 'ongoing' || project.status === 'needs_evidence') && (
                <div className={commits.length < 3 ? 'info-msg' : 'success-msg'} style={{ marginBottom: 16, fontSize: 13 }}>
                  {commits.length < 3
                    ? `Add at least 3 commits before submitting — submit button unlocks at 3 (${commits.length}/3). More commits means stronger evidence.`
                    : `Ready to submit! Feel free to add more commits — they'll appear in the project timeline on your certificate for visitors to see.`}
                </div>
              )}
              {commits.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-3)' }}>
                  {isOwner ? 'No commits yet. Add your first update above.' : 'No commits yet.'}
                </div>
              ) : (
                <div className="timeline">
                  {commits.map(c => {
                    const secsLeft = isOwner ? getEditSecondsLeft(c) : 0
                    const canEdit = isOwner && secsLeft > 0 && (project.status === 'ongoing' || project.status === 'needs_evidence')
                    const isEditing = editingCommitId === c.id
                    return (
                      <div key={c.id} className="timeline-item">
                        <div className="timeline-dot" />
                        <div className="timeline-meta" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span>{new Date(c.created_at).toLocaleString()}</span>
                          {canEdit && !isEditing && (
                            <span className="commit-edit-timer">
                              <button className="commit-edit-btn" onClick={() => {
                                setEditingCommitId(c.id)
                                setEditCommitMsg(c.message)
                                setEditCommitUrl(c.media_url || '')
                              }}>✏️ Edit</button>
                              <button className="commit-delete-btn" onClick={() => handleDeleteCommit(c)}>🗑 Delete</button>
                              <span className="commit-timer-pill">⏱ {formatCountdown(secsLeft)}</span>
                            </span>
                          )}
                          {!canEdit && isOwner && secsLeft === 0 && (project.status === 'ongoing' || project.status === 'needs_evidence') && (
                            <span className="commit-locked-pill">🔒 Locked</span>
                          )}
                        </div>

                        {isEditing ? (
                          <div className="commit-edit-form">
                            <textarea
                              rows={3}
                              value={editCommitMsg}
                              onChange={e => setEditCommitMsg(e.target.value)}
                              style={{ width: '100%', marginBottom: 8 }}
                            />
                            {(c.commit_type === 'link' || c.commit_type === 'video') && (
                              <input
                                type="url"
                                value={editCommitUrl}
                                onChange={e => setEditCommitUrl(e.target.value)}
                                placeholder="URL"
                                style={{ width: '100%', marginBottom: 8 }}
                              />
                            )}
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button className="btn btn-primary btn-sm" disabled={savingCommit} onClick={() => handleEditCommit(c)}>
                                {savingCommit ? <span className="loading-spinner" /> : 'Save'}
                              </button>
                              <button className="btn btn-ghost btn-sm" onClick={() => setEditingCommitId(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="timeline-msg">{c.message}</div>
                            {c.commit_type === 'photo' && c.media_url && (
                              <img src={c.media_url} alt="commit" className="timeline-photo" />
                            )}
                            {c.commit_type === 'link' && c.media_url && (
                              <a href={c.media_url} target="_blank" rel="noopener noreferrer" className="timeline-link">🔗 {c.media_url}</a>
                            )}
                            {c.commit_type === 'video' && c.media_url && (
                              <VideoEmbed url={c.media_url} />
                            )}
                          </>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="project-sidebar">
            <div className="card" style={{ marginBottom: 16 }}>
              <h4 style={{ marginBottom: 12 }}>Project info</h4>
              <div className="proj-info-row"><span>Started</span><span>{new Date(project.created_at).toLocaleDateString()}</span></div>
              {project.submitted_at && <div className="proj-info-row"><span>Submitted</span><span>{new Date(project.submitted_at).toLocaleDateString()}</span></div>}
              {project.verified_at && <div className="proj-info-row"><span>Verified</span><span>{new Date(project.verified_at).toLocaleDateString()}</span></div>}
              {project.verified_by_name && <div className="proj-info-row"><span>Reviewer</span><span>{project.verified_by_name}</span></div>}
              <div className="proj-info-row"><span>Commits</span><span>{commits.length}</span></div>
            </div>

            {isOwner && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h4 style={{ marginBottom: 12 }}>Visibility</h4>
                {['public', 'unlisted', 'private'].map(vis => {
                  const locked = (vis === 'unlisted' && !canUnlist) || (vis === 'private' && !canPrivate)
                  return (
                    <label key={vis} className={`vis-option ${project.visibility === vis ? 'selected' : ''}`} style={{ marginBottom: 8, opacity: locked ? 0.5 : 1 }}>
                      <input type="radio" name="vis" checked={project.visibility === vis} disabled={locked} onChange={() => handleVisibilityChange(vis)} />
                      <div className="vis-option-inner">
                        <strong>{VISIBILITY_LABELS[vis].icon} {VISIBILITY_LABELS[vis].label}</strong>
                        <span>{VISIBILITY_LABELS[vis].desc}</span>
                        {locked && <span className="coming-soon-flag" style={{ marginTop: 4 }}>Creator+</span>}
                      </div>
                    </label>
                  )
                })}
                <p className="hint" style={{ marginTop: 8 }}>Your visibility settings are never lost if you downgrade.</p>
              </div>
            )}

            {isOwner && (
              <div className="card" style={{ marginBottom: 16 }}>
                <h4 style={{ marginBottom: 12 }}>Certificate evidence</h4>
                <label className="evidence-toggle" style={{ alignItems: 'flex-start' }}>
                  <input
                    type="checkbox"
                    checked={!!project.show_evidence_on_cert}
                    onChange={e => handleEvidenceCertChange(e.target.checked)}
                  />
                  <span>
                    Show project timeline below certificate
                    <span className="hint" style={{ display: 'block', marginTop: 4 }}>
                      Visitors can scroll below the certificate to see the commits used as process evidence.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {project.status === 'verified' && isOwner && (
              <div className="card">
                <EmbedBadge uniqueId={project.unique_id} />
              </div>
            )}

            {project.status === 'verified' && (
              <div className="reviewer-trust" style={{ marginTop: 12 }}>
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <circle cx="7.5" cy="7.5" r="6.5" stroke="#c8f55a" strokeWidth="1.2"/>
                  <path d="M4.5 7.5l2 2 4-4" stroke="#c8f55a" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Reviewed by a human — not an algorithm.
              </div>
            )}

            {isOwner && (
              <div className="card" style={{ marginTop: 16, borderColor: 'var(--rejected, #e05c5c)' }}>
                <h4 style={{ marginBottom: 8, color: 'var(--rejected, #e05c5c)' }}>Danger zone</h4>
                {!showDeleteConfirm ? (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 12 }}>
                      Permanently delete this project and all its commits. This cannot be undone. <strong>It will not free up a project start slot.</strong>
                    </p>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ color: 'var(--rejected, #e05c5c)', borderColor: 'var(--rejected, #e05c5c)' }}
                      onClick={() => { setShowDeleteConfirm(true); setDeleteInput('') }}
                    >
                      Delete project…
                    </button>
                  </>
                ) : (
                  <>
                    <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 4 }}>
                      This will permanently delete <strong>{project.title}</strong> and all its commits. There is no undo. <strong>Your project start slot will not be returned.</strong>
                    </p>
                    <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 10 }}>
                      Type <strong>{project.title}</strong> to confirm:
                    </p>
                    <input
                      type="text"
                      placeholder={project.title}
                      value={deleteInput}
                      onChange={e => setDeleteInput(e.target.value)}
                      style={{ marginBottom: 10 }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        className="btn btn-sm"
                        style={{
                          background: deleteInput === project.title ? 'var(--rejected, #e05c5c)' : 'var(--bg-3)',
                          color: deleteInput === project.title ? '#fff' : 'var(--text-3)',
                          border: 'none',
                          cursor: deleteInput === project.title ? 'pointer' : 'not-allowed',
                          opacity: deleteInput === project.title ? 1 : 0.5,
                        }}
                        disabled={deleteInput !== project.title || deleting}
                        onClick={handleDeleteProject}
                      >
                        {deleting ? <span className="loading-spinner" /> : 'Confirm delete'}
                      </button>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => { setShowDeleteConfirm(false); setDeleteInput('') }}
                        disabled={deleting}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

const CATEGORIES = [
  'Writing & Fiction', 'Poetry & Lyrics', 'Journalism & Articles',
  'Digital Art & Illustration', 'Traditional / Physical Art', 'Photography',
  'Graphic Design', 'Music Composition', 'Music Production & Beats',
  'Voice Over & Narration', 'Podcast & Audio', 'Video Editing',
  'Animation & Motion', 'Film & Cinematography', 'Game Design',
  'Code & Software', 'Architecture & 3D Design', 'Craft & Handmade',
  'Sculpture & Ceramics', 'Other',
]
